import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Highlighter, Trash2, ExternalLink } from "lucide-react";
import { Link } from "react-router";
import { sessionService } from "../../application/SessionApplicationService";
import { listGroupPatientSessions } from "../../application/sessionUseCases";
import { listPatientHighlights, deleteSessionHighlight } from "../../application/sessionHighlights";
import { SessionNoteHighlight } from "../../domain/Session";
import { Session } from "../../domain/Session";

interface GroupedHighlights {
    session: Session;
    highlights: SessionNoteHighlight[];
}

export default function PatientHighlightsTab({
    patientId,
    isCouple = false,
    isGroup = false,
}: {
    patientId: string;
    isCouple?: boolean;
    /** A group's highlights are filed under the group, like a couple's under the couple. */
    isGroup?: boolean;
}) {
    const { t } = useTranslation();
    const [highlights, setHighlights] = useState<SessionNoteHighlight[]>([]);
    const [sessions, setSessions] = useState<Session[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!patientId) return;
        const sessionsFetch = isGroup
            ? listGroupPatientSessions(patientId, { limit: 200 })
            : isCouple
              ? sessionService.listSessionsByCouplePatient(patientId, { limit: 200 })
              : sessionService.listSessionsByPatient(patientId, { limit: 200 });
        Promise.all([
            listPatientHighlights(patientId),
            sessionsFetch,
        ]).then(([hRes, sRes]) => {
            setHighlights(hRes);
            setSessions(sRes.sessions);
        }).catch(() => {}).finally(() => setLoading(false));
    }, [patientId, isCouple, isGroup]);

    const handleDelete = async (highlightId: string) => {
        try {
            await deleteSessionHighlight(highlightId);
            setHighlights(prev => prev.filter(h => h.id !== highlightId));
        } catch {
            // ignore
        }
    };

    const grouped: GroupedHighlights[] = sessions
        .map(session => ({
            session,
            highlights: highlights.filter(h => h.session_id === session.id),
        }))
        .filter(g => g.highlights.length > 0)
        .sort((a, b) => new Date(b.session.date || 0).getTime() - new Date(a.session.date || 0).getTime());

    if (loading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (grouped.length === 0) {
        return (
            <div className="glass-effect glass-border border rounded-2xl p-12 text-center">
                <Highlighter className="w-10 h-10 text-content-tertiary mx-auto mb-4" />
                <p className="text-content-tertiary font-medium">
                    {t("session.noHighlights", "No highlights yet")}
                </p>
                <p className="text-sm text-content-tertiary mt-1">
                    {t("session.highlightsHint", "Select text in a session's notes to create a highlight")}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {grouped.map(({ session, highlights: groupHighlights }) => {
                return (
                    <div key={session.id} className="glass-effect glass-border border rounded-2xl overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-accent-tint flex items-center justify-center">
                                    <Highlighter className="w-4 h-4 text-accent-on-tint" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-content-primary">{session.title}</p>
                                    <p className="text-xs text-content-tertiary">
                                        {groupHighlights.length} {groupHighlights.length === 1
                                            ? t("session.highlight", "highlight")
                                            : t("session.highlights", "highlights")}
                                    </p>
                                </div>
                            </div>
                            <Link
                                to={`/session/${session.id}`}
                                className="flex items-center gap-1.5 text-xs text-accent-text hover:text-content-primary font-medium transition-colors duration-[--duration-fast]"
                            >
                                {t("common.view", "View")}
                                <ExternalLink className="w-3 h-3" />
                            </Link>
                        </div>

                        <div className="divide-y divide-border-subtle">
                            {groupHighlights.map(h => (
                                <div key={h.id} className="flex items-start gap-4 px-6 py-4 group hover:bg-surface-sunken transition-colors duration-[--duration-fast]">
                                    <span className="flex-1 text-sm text-content-secondary leading-relaxed">
                                        <span className="bg-accent-tint text-accent-on-tint rounded px-0.5">
                                            {h.text}
                                        </span>
                                    </span>
                                    <button
                                        onClick={() => handleDelete(h.id)}
                                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-content-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all flex-shrink-0"
                                        title={t("common.delete", "Delete")}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
